/**
 * balance.ts — balance sheets + metric rollups. Pure functions, no React.
 *
 * Two outputs the sidebar needs:
 *  - `BalanceSheet` — a two-column {in, out} ledger per node, plus a global
 *    sheet that sums across the whole graph. This is the spec's "balance sheet
 *    is always two columns and this is what we are balancing."
 *  - `computeMetrics` — turns the per-node quantities in a tick into the
 *    headline numbers (revenue, costs, fulfilment, backlog, cash) the metric
 *    cards show, using the `MetricSpec.rollup` rules from the scenario bundle.
 *
 * Engine purity: balance.ts reads only the Graph (for node groups) and the
 * TickPayload (for the numbers). It does not import any scenario by name.
 */

import type {
  Graph,
  MetricSpec,
  NodeGroup,
  NodeId,
  TickPayload,
} from './types';

// ─── Balance sheet ──────────────────────────────────────────────────────────
export interface BalanceRow {
  nodeId: NodeId;
  label: string;
  inflow: number;
  outflow: number;
  /** net = inflow - outflow. Positive = the node gained this tick; negative =
   *  it spent. The two columns always balance to this. */
  net: number;
  stock: number;
}

export interface BalanceSheet {
  rows: BalanceRow[];
  global: { inflow: number; outflow: number; net: number };
}

/** Build the balance sheet for a tick. Node labels come from the graph; the
 *  numbers come from the tick. Rows are ordered as in the graph's node list
 *  so the sidebar is stable across ticks. */
export function buildBalanceSheet(graph: Graph, tick: TickPayload): BalanceSheet {
  const rows: BalanceRow[] = [];
  let gIn = 0;
  let gOut = 0;
  for (const n of graph.nodes) {
    const v = tick.nodeValues[n.id];
    if (!v) continue;
    const inflow = v.inflow;
    const outflow = v.outflow;
    const net = inflow - outflow;
    rows.push({
      nodeId: n.id,
      label: n.label,
      inflow,
      outflow,
      net,
      stock: v.stock,
    });
    gIn += inflow;
    gOut += outflow;
  }
  return { rows, global: { inflow: gIn, outflow: gOut, net: gIn - gOut } };
}

// ─── Metrics ────────────────────────────────────────────────────────────────


import { evalExpr } from './expr/walker';

/** Compute every metric for a tick. Prefers pre-computed values from the tick
 *  (the backend / replay file may carry `metricValues`); falls back to evaluating
 *  the CEL expression locally. */
export function computeMetrics(
  graph: Graph,
  tick: TickPayload,
  specs: MetricSpec[],
): Record<string, number> {
  const scope = buildGlobalScope(graph, tick);

  const out: Record<string, number> = {};
  for (const spec of specs) {
    if (tick.metricValues && spec.key in tick.metricValues) {
      out[spec.key] = tick.metricValues[spec.key];
      continue;
    }
    try {
      const localScope = spec.nodeId 
        ? { ...scope, self: scope.nodes[spec.nodeId] }
        : scope;
      const res = evalExpr(spec.expr, localScope);
      out[spec.key] = typeof res === 'number' && !Number.isNaN(res) ? res : NaN;
    } catch {
      out[spec.key] = NaN;
    }
  }
  return out;
}

function buildGlobalScope(graph: Graph, tick: TickPayload) {
  const nodes: Record<string, any> = {};
  const edges: Record<string, any> = {};
  for (const e of graph.edges) {
    const ep: Record<string, number> = { flow: tick.edgeFlows?.[e.id] ?? 0 };
    for (const param of e.params) ep[param.key] = param.value;
    edges[e.id] = ep;
  }
  for (const n of graph.nodes) {
    const v = tick.nodeValues[n.id] ?? { inflow: 0, outflow: 0, stock: 0 };
    const p: Record<string, number> = {};
    for (const spec of n.params) p[spec.key] = spec.value;
    for (const spec of n.gate?.params ?? []) p[spec.key] = spec.value;
    for (const spec of n.source?.distParams ?? []) p[spec.key] = spec.value;
    nodes[n.id] = { ...p, stock: v.stock, inflow: v.inflow, outflow: v.outflow };
    if (v.state) {
      for (const [k, sv] of Object.entries(v.state)) {
        nodes[n.id][k] = sv;
      }
    }
  }
  return { nodes, edges };
}

// ─── Node-group aggregation ─────────────────────────────────────────────────
//
// A node group's balance sheet + metrics are COLLECTED from its member nodes.
// Membership is spatial (see src/graph/groups.ts); callers pass the resolved
// member id set, so these functions stay free of layout concerns. The pattern
// mirrors buildBalanceSheet's `global` accumulator and computeMetrics's
// scope-swap: no per-node state is invented, only summed.

/** Aggregated quantities for a group: the sum of member-node inflow/outflow/
 *  stock. Same shape as BalanceSheet.global plus a stock total. */
export interface GroupBalance {
  inflow: number;
  outflow: number;
  net: number;
  stock: number;
}

/** Sum the balance-sheet quantities across a set of member nodes. Mirrors the
 *  `global` accumulator in buildBalanceSheet, filtered to members. Nodes that
 *  have no tick value yet (brand-new) are skipped, not zeroed. */
export function buildGroupBalanceSheet(
  graph: Graph,
  tick: TickPayload,
  memberIds: Set<NodeId>,
): GroupBalance {
  let inflow = 0;
  let outflow = 0;
  let stock = 0;
  for (const n of graph.nodes) {
    if (!memberIds.has(n.id)) continue;
    const v = tick.nodeValues[n.id];
    if (!v) continue;
    inflow += v.inflow;
    outflow += v.outflow;
    stock += v.stock;
  }
  return { inflow, outflow, net: inflow - outflow, stock };
}

/** Evaluate a group's `computedMetrics`. The scope is the global scope (so
 *  `nodes.bank.stock` cross-refs work) with `self` overlaid as the SUMMED
 *  member state. Sums stock/inflow/outflow plus every numeric member state key
 *  (consumed, overflow, computed-metric outputs, …) so a group metric like
 *  `self.stock` or `self.inflow - self.outflow` works without the author having
 *  to enumerate members. Non-numeric / missing keys are ignored. */
export function computeGroupMetrics(
  graph: Graph,
  tick: TickPayload,
  group: NodeGroup,
  memberIds: Set<NodeId>,
): Record<string, number> {
  const base = buildGlobalScope(graph, tick);
  // Sum each numeric leaf across members into the synthetic `self`.
  const self: Record<string, number> = { stock: 0, inflow: 0, outflow: 0 };
  const seen = new Set<string>();
  for (const id of memberIds) {
    const nodeScope = base.nodes[id];
    if (!nodeScope) continue;
    for (const [k, v] of Object.entries(nodeScope)) {
      if (typeof v !== 'number' || !Number.isFinite(v)) continue;
      // Only promote a key to the summed `self` if it looks like a state
      // quantity, not a node param (summing two nodes' `capacity` is
      // meaningless). stock/inflow/outflow are always summed (above); state
      // keys are anything not declared as a ParamSpec on the node.
      const node = graph.nodes.find((n) => n.id === id);
      const isParam =
        !!node &&
        [...node.params, ...(node.gate?.params ?? []), ...(node.source?.distParams ?? [])].some(
          (s) => s.key === k,
        );
      if (isParam) continue;
      self[k] = (self[k] ?? 0) + v;
      seen.add(k);
    }
  }
  void seen; // (kept for future "which keys aggregated" introspection)
  const scope = { ...base, self };
  const out: Record<string, number> = {};
  for (const m of group.computedMetrics ?? []) {
    try {
      const res = evalExpr(m.expr, scope);
      out[m.key] = typeof res === 'number' && !Number.isNaN(res) ? res : NaN;
    } catch {
      out[m.key] = NaN;
    }
  }
  return out;
}
